// Licence: CC BY 4.0 in plain words, the attribution format, "former contributor", Wizard fillers,
// and how to credit a stretch of the song or a day's replay.
import { CONTACT, LegalLayout, LegalLink } from "./LegalLayout";

const CC_BY = "https://creativecommons.org/licenses/by/4.0/";

export function Licence() {
  return (
    <LegalLayout slug="licence" title="Licence">
      <h2 id="summary">Every song is CC BY 4.0</h2>
      <p>
        Every loop contributors write for The Infinite is licensed by its contributor under the{" "}
        <strong>Creative Commons Attribution 4.0 International licence (CC BY 4.0)</strong>. The full legal text is at{" "}
        <a href={CC_BY} rel="license noopener">
          {CC_BY}
        </a>
        .
      </p>

      <h2 id="plain">What that means, in plain words</h2>
      <p>You may, for any purpose, also commercially:</p>
      <ul>
        <li>
          <strong>share</strong> a loop — copy it, play it, record it, put it in a stream, video or set;
        </li>
        <li>
          <strong>adapt</strong> it — remix it, change it, build your own track on it.
        </li>
      </ul>
      <p>As long as you:</p>
      <ul>
        <li>
          <strong>give credit</strong> to the contributor in the form below;
        </li>
        <li>
          <strong>link to the licence</strong>;
        </li>
        <li>
          <strong>say if you changed it</strong> (for example “remixed” or “edited”);
        </li>
        <li>
          <strong>don't suggest</strong> that the contributor or The Infinite endorses you or your use;
        </li>
        <li>
          <strong>don't add restrictions</strong> (legal terms or technical measures) that stop others from doing what
          the licence allows.
        </li>
      </ul>
      <p className="muted">
        This summary is not a substitute for the licence. If they differ, the licence text applies.
      </p>

      <h2 id="credit">How to credit a song</h2>
      <p>Use this format:</p>
      <p className="legal-format">
        “&lt;title or Loop&gt;” by &lt;nickname&gt;, The Infinite, CC BY 4.0, &lt;loop page URL&gt;
      </p>
      <p>For example:</p>
      <p className="legal-format">
        “Night Bus” by mira_k, The Infinite, CC BY 4.0, https://the-infinite.stream/loop/l_7kx2qd
      </p>
      <ul>
        <li>Use the song's title if it has one; otherwise write “Song”.</li>
        <li>
          Every loop has its own page under <code>/loop/…</code>; its address is the loop page URL. The page shows the
          title, the contributor's nickname and the Wizard mark.
        </li>
        <li>
          Add a link to the licence where your medium allows it (<a href={CC_BY}>{CC_BY}</a>). Where it doesn't (for
          example spoken in a stream), “CC BY 4.0” plus the loop page is a reasonable way to point there.
        </li>
        <li>If you changed the song, say so: “… remixed by &lt;your name&gt;”.</li>
        <li>
          Loops made with help from the Wizard carry a mark such as “38 % Wizard”. You don't have to repeat it, but we'd
          appreciate it if you did.
        </li>
      </ul>

      <h2 id="former">“former contributor”</h2>
      <p>
        When contributors delete their account, the loops of theirs that have already played stay in the song, because
        the CC BY 4.0 licence can't be revoked. At their request their nickname is removed, and those loops are credited
        to <strong>“former contributor”</strong>:
      </p>
      <p className="legal-format">
        “&lt;title or Loop&gt;” by former contributor, The Infinite, CC BY 4.0, &lt;loop page URL&gt;
      </p>
      <p>
        If you used such a loop before the account was deleted, please update the credit where it is reasonably
        practical.
      </p>

      <h2 id="fillers">Wizard fillers</h2>
      <p>
        Slots that no contributor filled play loops generated automatically by the Wizard. They are offered under CC BY
        4.0 as well, as far as any rights exist in them. Credit them as:
      </p>
      <p className="legal-format">“Loop” by The Infinite (Wizard filler), CC BY 4.0, &lt;loop page URL&gt;</p>

      <h2 id="song">Crediting the song or a day's replay</h2>
      <p>
        A recording of The Infinite — a live stretch of the song or a replay of a past day — contains many loops by many
        people, and each of them should be credited. You don't have to list every loop in your recording itself: CC BY
        4.0 allows credit “in any reasonable manner” for your medium, including a link to a page that contains the
        credits. Every day has a page listing each slot with its title, contributor and Wizard mark, so you can credit
        like this:
      </p>
      <p className="legal-format">
        The Infinite, &lt;date&gt;, &lt;start–end time with time zone&gt;, loops by the contributors listed at &lt;day
        page URL&gt;, CC BY 4.0, {CC_BY}
      </p>
      <p>For example:</p>
      <p className="legal-format">
        The Infinite, 12 March 2027, 20:00–21:00 UTC, loops by the contributors listed at
        https://the-infinite.stream/day/2027-03-12, CC BY 4.0, {CC_BY}
      </p>
      <p>
        If your recording is short or only uses a few loops, credit those loops individually in the loop format above.
        Say if you edited, cut or remixed the recording.
      </p>

      <h2 id="other">What else is (and isn't) covered</h2>
      <ul>
        <li>
          <strong>Sounds:</strong> loops are played with the day's drum kit and instruments, which The Infinite
          generates entirely in code. Recordings of the song contain these sounds; they are licensed under CC BY 4.0,
          credited to The Infinite (the credit line above covers them).
        </li>
        <li>
          <strong>Lineups:</strong> the order of songs in each day's lineup is generated automatically; we claim no
          rights in it.
        </li>
        <li>
          <strong>Not covered:</strong> the name The Infinite, the website's design and software, and the texts on these
          pages. See the <LegalLink to="imprint">Imprint</LegalLink>.
        </li>
      </ul>
      <p>
        Under the <LegalLink to="terms">Terms of Use</LegalLink>, contributors confirm that their loops are their own
        work, and every loop is checked automatically for copied melodies. We can't guarantee, however, that no loop
        infringes someone else's rights. If you believe a loop copies your work, use the Report button on the song or
        {CONTACT}.
      </p>
    </LegalLayout>
  );
}
